import { useState, useEffect, useCallback } from 'react';

interface UseCollapsedFiltersOptions {
  storageKey: string;
  defaultCollapsed?: boolean;
}

interface UseCollapsedFiltersReturn {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  toggleCollapsed: () => void;
  isInitialized: boolean;
}

/**
 * Hook for managing collapsed state of filter bars with localStorage persistence.
 *
 * @param options.storageKey - Unique key for localStorage persistence
 * @param options.defaultCollapsed - Initial collapsed state if no stored value (default: false)
 * @returns Object with isCollapsed state, setter, toggle function, and initialization status
 */
export function useCollapsedFilters({
  storageKey,
  defaultCollapsed = false,
}: UseCollapsedFiltersOptions): UseCollapsedFiltersReturn {
  const [isCollapsed, setIsCollapsedState] = useState<boolean>(defaultCollapsed);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Read from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored !== null) {
        setIsCollapsedState(JSON.parse(stored));
      }
    } catch (error) {
      console.warn(`Failed to read collapsed state from localStorage (${storageKey}):`, error);
    }
    setIsInitialized(true);
  }, [storageKey]);

  // Persist to localStorage when state changes (after initialization)
  const setIsCollapsed = useCallback((collapsed: boolean) => {
    setIsCollapsedState(collapsed);
    try {
      localStorage.setItem(storageKey, JSON.stringify(collapsed));
    } catch (error) {
      console.warn(`Failed to save collapsed state to localStorage (${storageKey}):`, error);
    }
  }, [storageKey]);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed(!isCollapsed);
  }, [isCollapsed, setIsCollapsed]);

  return {
    isCollapsed,
    setIsCollapsed,
    toggleCollapsed,
    isInitialized,
  };
}
